import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { isValidPhoneNumber } from "react-phone-number-input";
import { toast } from "sonner";

import { QRCodeSVG } from "qrcode.react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PhoneInput } from "@/components/ui/phone-input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import CircularProgress from "@/components/Common/CircularProgress";

import { useAuthContext } from "@/hooks/useAuthUser";

import { TokenData } from "@/types/otp/otp";
import {
  ElpakoMethod,
  careSessionFromApp,
  elpakoPoll,
  elpakoStart,
  openAppWebLogin,
  webLoginQrPayload,
} from "@/types/patientAuth/patientAuth";

type Method = "qr" | ElpakoMethod;

const PERSONAL_CODE_RE = /^\d{11}$/;
const POLL_INTERVAL_MS = 3000;

export default function PatientAuthMethods({
  redirectUrl,
}: {
  redirectUrl: string;
}) {
  const { t } = useTranslation();
  const { patientLogin } = useAuthContext();
  const [method, setMethod] = useState<Method>("qr");

  const finishLogin = (access: string, phoneNumber = "") => {
    const tokenData: TokenData = {
      token: access,
      phoneNumber,
      createdAt: new Date().toISOString(),
    };
    patientLogin(tokenData, redirectUrl);
  };

  return (
    <Tabs value={method} onValueChange={(v) => setMethod(v as Method)}>
      <TabsList className="flex w-full">
        <TabsTrigger className="flex-1 px-1 text-xs sm:text-sm" value="qr">
          {t("login_with_avitra_app")}
        </TabsTrigger>
        <TabsTrigger
          className="flex-1 px-1 text-xs sm:text-sm"
          value="smart_id"
        >
          {t("login_smart_id")}
        </TabsTrigger>
        <TabsTrigger className="flex-1 px-1 text-xs sm:text-sm" value="mobile">
          {t("login_mobile_id")}
        </TabsTrigger>
      </TabsList>

      <TabsContent value="qr">
        <QrLogin active={method === "qr"} onAccess={finishLogin} />
      </TabsContent>
      <TabsContent value="smart_id">
        <ElpakoLogin method="smart_id" onAccess={finishLogin} />
      </TabsContent>
      <TabsContent value="mobile">
        <ElpakoLogin method="mobile" onAccess={finishLogin} />
      </TabsContent>
    </Tabs>
  );
}

/** Kas kiek atnaujinamas QR kodas (backend sesijos TTL yra ilgesnis). */
const QR_REFRESH_MS = 5 * 60 * 1000;

/** QR: scan with the Avitra app to authorize this browser session. */
function QrLogin({
  active,
  onAccess,
}: {
  active: boolean;
  onAccess: (access: string) => void;
}) {
  const { t } = useTranslation();
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [status, setStatus] = useState<
    "connecting" | "ready" | "approving" | "error"
  >("connecting");
  const [scanned, setScanned] = useState(false);
  const [qrAt, setQrAt] = useState(0);
  const [now, setNow] = useState(0);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!active) return;
    setStatus("connecting");
    setSessionId(null);
    setScanned(false);
    const handle = openAppWebLogin({
      onSession: (id) => {
        setSessionId(id);
        setStatus("ready");
        setQrAt(Date.now());
        setNow(Date.now());
      },
      onScanned: () => setScanned(true),
      onCancelled: () => setScanned(false),
      onApproved: async (appToken) => {
        setStatus("approving");
        try {
          const { access } = await careSessionFromApp(appToken);
          onAccess(access);
        } catch (e) {
          toast.error(
            e instanceof Error ? e.message : t("something_went_wrong"),
          );
          setStatus("error");
        }
      },
      onError: () => setStatus("error"),
    });
    return () => handle.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, nonce]);

  // 1 s laikrodis: timeris / status baras + QR atnaujinimas kas 5 min.
  useEffect(() => {
    if (!active || status !== "ready" || scanned || !qrAt) return;
    const id = window.setInterval(() => {
      const ts = Date.now();
      setNow(ts);
      if (ts - qrAt >= QR_REFRESH_MS) setNonce((n) => n + 1);
    }, 1000);
    return () => window.clearInterval(id);
  }, [active, status, scanned, qrAt]);

  const remaining = Math.max(0, QR_REFRESH_MS - (now - qrAt));
  const pct = qrAt ? Math.max(0, Math.min(1, remaining / QR_REFRESH_MS)) : 1;
  const mmss = `${Math.floor(remaining / 60000)}:${String(
    Math.floor((remaining % 60000) / 1000),
  ).padStart(2, "0")}`;

  return (
    <div className="mt-4 flex flex-col items-center gap-4 rounded-lg border border-gray-200 p-8 shadow-sm">
      <p className="text-center text-sm text-secondary-700">
        {scanned ? t("confirm_in_avitra_app") : t("scan_qr_with_avitra_app")}
      </p>
      <div className="relative flex size-48 items-center justify-center">
        {status === "ready" && sessionId ? (
          <>
            <div
              className={
                scanned ? "opacity-40 blur-sm transition" : "transition"
              }
            >
              <QRCodeSVG value={webLoginQrPayload(sessionId)} size={176} />
            </div>
            {scanned && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
                <div className="flex size-11 items-center justify-center rounded-full bg-primary-500 text-lg font-bold text-white">
                  ✓
                </div>
                <span className="text-sm font-semibold text-secondary-800">
                  {t("qr_scanned")}
                </span>
              </div>
            )}
          </>
        ) : status === "error" ? (
          <Button variant="outline" onClick={() => setNonce((n) => n + 1)}>
            {t("retry")}
          </Button>
        ) : (
          <CircularProgress />
        )}
      </div>
      {status === "ready" && sessionId && !scanned && (
        <div className="flex w-44 flex-col items-center gap-1">
          <div className="h-1 w-full overflow-hidden rounded-full bg-secondary-200">
            <div
              className="h-full rounded-full bg-primary-500 transition-[width] duration-1000 ease-linear"
              style={{ width: `${pct * 100}%` }}
            />
          </div>
          <span className="text-xs font-medium tabular-nums text-secondary-600">
            {t("qr_refreshes_in", { time: mmss })}
          </span>
        </div>
      )}
      {status === "approving" && (
        <p className="text-sm text-secondary-700">{t("logging_in")}</p>
      )}
    </div>
  );
}

/** Elpako Smart-ID / m. parašas: enter personal code, confirm the control code. */
function ElpakoLogin({
  method,
  onAccess,
}: {
  method: ElpakoMethod;
  onAccess: (access: string, phoneNumber?: string) => void;
}) {
  const { t } = useTranslation();
  const [personalCode, setPersonalCode] = useState("");
  const [phone, setPhone] = useState("");
  const [session, setSession] = useState<string | null>(null);
  const [controlCode, setControlCode] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  useEffect(() => stopPolling, []);

  const reset = () => {
    stopPolling();
    setSession(null);
    setControlCode(null);
    setPending(false);
  };

  const start = async () => {
    setError("");
    if (!PERSONAL_CODE_RE.test(personalCode)) {
      setError(t("invalid_personal_code"));
      return;
    }
    if (method === "mobile" && !isValidPhoneNumber(phone)) {
      setError(t("phone_number_validation_error"));
      return;
    }
    setPending(true);
    try {
      const res = await elpakoStart({
        method,
        personalCode,
        phone: method === "mobile" ? phone : undefined,
      });
      setSession(res.session);
      setControlCode(res.control_code);
      beginPolling(res.session);
    } catch (e) {
      setPending(false);
      setError(e instanceof Error ? e.message : t("something_went_wrong"));
    }
  };

  const beginPolling = (sess: string) => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const res = await elpakoPoll({
          method,
          session: sess,
          phone: method === "mobile" ? phone : undefined,
        });
        if (res.status === "ok" && res.access) {
          stopPolling();
          onAccess(res.access, method === "mobile" ? phone : "");
        } else if (res.status === "error") {
          stopPolling();
          setPending(false);
          setError(res.detail || t("identity_verification_failed"));
          setSession(null);
          setControlCode(null);
        }
      } catch (e) {
        stopPolling();
        setPending(false);
        setError(e instanceof Error ? e.message : t("something_went_wrong"));
        setSession(null);
        setControlCode(null);
      }
    }, POLL_INTERVAL_MS);
  };

  if (session) {
    return (
      <div className="mt-4 flex flex-col items-center gap-4 rounded-lg border border-gray-200 p-8 text-center shadow-sm">
        <p className="text-sm text-secondary-700">
          {t("confirm_control_code")}
        </p>
        {controlCode && (
          <div className="rounded-lg bg-primary-50 px-6 py-3 text-3xl font-bold tracking-widest text-primary-700">
            {controlCode}
          </div>
        )}
        <div className="flex items-center gap-2 text-sm text-secondary-600">
          <CircularProgress className="size-4" />
          {t("waiting_for_confirmation")}
        </div>
        <Button variant="link" className="h-auto p-0" onClick={reset}>
          {t("cancel")}
        </Button>
      </div>
    );
  }

  return (
    <div className="mt-4 flex flex-col gap-4 rounded-lg border border-gray-200 p-8 shadow-sm">
      <div className="space-y-2">
        <Label htmlFor="personal_code">{t("personal_code")}</Label>
        <Input
          id="personal_code"
          inputMode="numeric"
          maxLength={11}
          value={personalCode}
          placeholder={t("enter_personal_code")}
          onChange={(e) => {
            setPersonalCode(e.target.value.replace(/\D/g, ""));
            setError("");
          }}
        />
      </div>
      {method === "mobile" && (
        <div className="space-y-2">
          <Label>{t("phone_number")}</Label>
          <PhoneInput
            value={phone}
            onChange={(value) => {
              setPhone(value ?? "");
              setError("");
            }}
            placeholder={t("enter_phone_number")}
          />
        </div>
      )}
      {error && <p className="text-sm text-red-500">{error}</p>}
      <Button
        variant="primary"
        className="w-full"
        disabled={pending}
        onClick={start}
      >
        {pending ? <CircularProgress className="text-white" /> : t("login")}
      </Button>
    </div>
  );
}
